import type { UniqueIdentifier } from '@dnd-kit/core';
import { useEffect, useMemo, useState } from 'react';
import { ItemTypeChoices } from '../../../../__generated__/graphql';
import {
  flattenTree,
  removeChildrenOf,
} from '../../../../helpers/dnd/utilities';
import { useAppDispatch, useAppSelector } from '../../../../helpers/reduxHooks';
import { handleQuotationGroupCollapse, collapseGroups } from '../../../../helpers/utils';
import { setMaximumSearchUncollapsedDepth } from '../../../../redux/searchSlice';
import type { FlattenedItem } from '../../../shared/dnd/types';
import GroupItem from './row/group/GroupItem';
import ProductItem from './row/product/ProductItem';
import ServiceItem from './row/service/ServiceItem';

interface Props {
  defaultItems: FlattenedItem[];
  indentationWidth?: number;
  isSearchResult: boolean;
}

export default function SearchList(props: Props) {
  const {
    indentationWidth = 25, defaultItems, isSearchResult,
  } = props;
  const [items, setItems] = useState(() => collapseGroups(defaultItems));
  const pinnedItems = useAppSelector(state => state.search.pinnedItems);
  const dispatch = useAppDispatch();

  const flattenedItems = useMemo(() => {
    const flattenedTree = flattenTree(items);
    const collapsedItems = flattenedTree.reduce<any>(
      (acc, { children, collapsed, id }) =>
        collapsed && children.length ? [...acc, id] : acc,
      [],
    );
    return removeChildrenOf(
      flattenedTree,
      collapsedItems,
    );
  }, [items]);

  function handleCollapse(idsToProcess: UniqueIdentifier[]) {
    setItems((itemsToUpdate) =>
      handleQuotationGroupCollapse(itemsToUpdate, idsToProcess),
    );
  }

  function hasPinnedParent(id: UniqueIdentifier, pinnedItemsInput: FlattenedItem[]) {
    // recursive function to search through children
    function searchChildren(children: FlattenedItem[], nestedId: UniqueIdentifier) {
      for (const child of children) {
        if (child.id === nestedId || searchChildren(child.children, nestedId)) {
          return true;
        }
      }
      return false;
    }

    // search through the children of each pinned item
    for (const item of pinnedItemsInput) {
      if (searchChildren(item.children, id)) {
        return true;
      }
    }
    return false;
  }
  
  useEffect(() => {
    const deepestSearchUncollapsedLevel = Math.max(...flattenedItems.map(item => item.depth));
    dispatch(setMaximumSearchUncollapsedDepth(deepestSearchUncollapsedLevel));
  }, [flattenedItems, dispatch]);

  useEffect(() => {
    // Update local items each time we have updated items incoming outside this component.
    setItems(defaultItems);
  }, [defaultItems]);

  useEffect(() => {
    // Update local items each time we have updated items incoming outside this component.
    const updatedItems = collapseGroups(defaultItems);
    setItems(updatedItems);
  }, [defaultItems]);

  
  return (
    <>
      {flattenedItems.map((item) => (
        <div key={item.id}>
          {item.itemType === ItemTypeChoices.Product && (
            <ProductItem
              hasPinnedParent={hasPinnedParent(item.id, pinnedItems)}
              indentationWidth={indentationWidth}
              isPinned={!!pinnedItems.find(pinnedItem => pinnedItem.id == item.id)}
              isSearchResult={isSearchResult}
              item={item}
            />
          )}
          {item.itemType === ItemTypeChoices.Group && (
            <GroupItem
              collapsed={Boolean(item.collapsed && item.children.length)}
              hasPinnedParent={hasPinnedParent(item.id, pinnedItems)}
              indentationWidth={indentationWidth}
              isPinned={!!pinnedItems.find(pinnedItem => pinnedItem.id == item.id)}
              isSearchResult={isSearchResult}
              item={item}
              onCollapse={
                item.children.length
                  ? () => handleCollapse([item.id])
                  : undefined
              }
            />
          )}
          {item.itemType === ItemTypeChoices.Service && (
            <ServiceItem
              hasPinnedParent={hasPinnedParent(item.id, pinnedItems)}
              indentationWidth={indentationWidth}
              isPinned={!!pinnedItems.find(pinnedItem => pinnedItem.id == item.id)}
              isSearchResult={isSearchResult}
              item={item}
            />
          )}
        </div>
      ))}
    </>
  );
}
